import { FC, useEffect, useState } from "react";
import { useHistory, RouteComponentProps } from "react-router-dom"
import { BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { QESButton } from "@app/shared/qes-standards";
import { ProviderSetVersionSummaryType, ProviderSetDataSortColumn } from "./enumerations";
import { PageSettings, ProviderDetailsGrid, ProviderDetailsGridOnUpdateResponse } from "./ProviderDetailsGrid";
import { SortDirections } from "@app/shared/waffles";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ExportService } from "@app/exports";
import { ExportProvidersModel } from "@app/exports/exportModels";
import { handleToastSuccess, handleToastError } from "@app/report-customization/build-reports/helpers";

interface ProviderDetailsMatchProps {
  providerSetId?: string,
  versionId?: string,
  summaryType: ProviderSetVersionSummaryType,
  filter?: string,
}

type ProviderDetailsProps = RouteComponentProps<ProviderDetailsMatchProps, unknown, unknown>;

export const ProviderDetails: FC<React.PropsWithChildren<ProviderDetailsProps>> = (props: ProviderDetailsProps) => {

  const exportService = new ExportService();
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();

  const [hasRecords, setHasRecords] = useState(false);
  const [providerSetName, setProviderSetName] = useState("");
  const [pageSettings, setPageSettings] = useState<PageSettings>(!!urlParams.get("pageSet") ? JSON.parse(urlParams.get("pageSet")) :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.Asc,
        sortedColumn: ProviderSetDataSortColumn.NPI,
      },
    });
  const [providerDetailBreadCrumbName, setProviderDetailBreadCrumbName] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [dlFileBaseName, setDlFileBaseName] = useState("");

  useEffect(() => {
    let pageTitleSuffix: string;
    const summaryType = (!!props.match.params.summaryType ? props.match.params.summaryType.toString() : "");

    switch (summaryType.toLowerCase()) {
      case ProviderSetVersionSummaryType.ByState.toLowerCase():
        setProviderDetailBreadCrumbName("By State");
        pageTitleSuffix = `(By State - ${props.match.params.filter.toUpperCase()})`;
        break;
      case ProviderSetVersionSummaryType.BySpecCode.toLowerCase():
        setProviderDetailBreadCrumbName("By Specialty");
        pageTitleSuffix = `(By Specialty - ${props.match.params.filter})`;
        break;
      case ProviderSetVersionSummaryType.ByGeocode.toLowerCase():
        setProviderDetailBreadCrumbName("By Geocode Result");
        pageTitleSuffix = `(By Geocode Result - ${props.match.params.filter})`;
        break;
      case ProviderSetVersionSummaryType.SpecCodeCountBreakdown.toLowerCase():
        setProviderDetailBreadCrumbName("Specialty Inspection");
        pageTitleSuffix = `(Providers with ${props.match.params.filter} ${props.match.params.filter === "1" ? "specialty" : "specialties"})`;
        break;
      case ProviderSetVersionSummaryType.AddressCountBreakdown.toLowerCase():
        setProviderDetailBreadCrumbName("Address Inspection");
        pageTitleSuffix = `(Providers with ${props.match.params.filter} ${props.match.params.filter === "1" ? "address" : "addresses"})`;
        break;
      case ProviderSetVersionSummaryType.ByNPIInspection.toLowerCase():
        setProviderDetailBreadCrumbName("NPI Inspection");
        pageTitleSuffix = `(By NPI Inspection - ${props.match.params.filter})`;
        break;
      default:
        setProviderDetailBreadCrumbName("Overall Summary");
        pageTitleSuffix = "Overall Summary";
        break;
    }

    setPageTitle(`${providerSetName} ${pageTitleSuffix}`);
    setDlFileBaseName(pageTitleSuffix);
  }, [providerSetName]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
      searchParams.set("pageSet", JSON.stringify(pageSettings));
    } else {
      searchParams.delete("pageSet");
    }

    history.replace(history.location.pathname + "?" + searchParams.toString());
  }, [pageSettings]);

  const handleDownload = async () => {
    let summaryType = props.match.params.summaryType;
    if (!summaryType)
      summaryType = ProviderSetVersionSummaryType.NoFilter

    const requestModel: ExportProvidersModel = {
      fileBaseName: dlFileBaseName,
      providerFilter: props.match.params.filter,
      providerSetId: parseInt(props.match.params.providerSetId),
      providerSetVersionId: parseInt(props.match.params.versionId),
      summaryType: summaryType
    }
    await exportService.exportProviders(requestModel)
      .then(() => handleToastSuccess("Download"),
        () => handleToastError("Download")
      )
  }

  return (
    <>
      {(
        <Page documentTitle={`${pageTitle || "Loading"} - Providers`}>
          <Breadcrumbs
            template={BreadcrumbTemplateEnum.ProviderSets}
            items={[
              {
                iconClass: "fa-user-md",
                link: `/provider-sets/${props.match.params.providerSetId}/detail`,
                text: providerSetName,
              },
              {
                text: providerDetailBreadCrumbName
              },
            ]}
          />
          <PageInner>
            <PageHeader title={pageTitle} iconName={QesIcons.FontAwesome.UserMd}>
            </PageHeader>
            <PageSection>
              <ProviderDetailsGrid
                providerSetId={props.match.params.providerSetId}
                versionId={props.match.params.versionId}
                summaryType={props.match.params.summaryType}
                filter={props.match.params.filter}
                pageSettings={pageSettings}
                take={500}
                onUpdate={(response: ProviderDetailsGridOnUpdateResponse) => {
                  setHasRecords(response.hasRecords);
                  setProviderSetName(response.providerSetName);
                  setPageSettings(response.pageSettings);
                }
                }
              />
              {hasRecords ?
                (<QESButton qesType="primary" qesTestId="provider-details__download-button" className="mt-3" onClick={() => handleDownload()}><i className="fa fa-download pr5" />Download</QESButton>)
                : null
              }
            </PageSection>
          </PageInner>
        </Page>
      )}
    </>
  );
}
